import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Person as PersonIcon,
  Casino as CasinoIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Forum as ForumIcon,
  Email as EmailIcon,
  AddCircle as AddCircleIcon,
  DeleteForever as DeleteForeverIcon,
} from "@mui/icons-material";

import { formatDate } from "utils/helperFunctions";


import RequestsModal from "./ManageGame/Modals/RequestsModal";

interface Request {
  id: string;
  gameId: string;
  gameName: string;
  senderId: string;
  senderName: string;
  timeStamp: any;
  status: string;
}

function RequestsComponents() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const requests = useSelector((state: any) => state.requests.receivedRequests);
  const navigate = useNavigate();

  const [modalDets, setModalDets] = useState({
    open: false,
    status: "",
    reqData: {},
  });

  const headerCellStyle = {
    fontWeight: 500,
    fontSize: isMobile ? "0.75rem" : "0.875rem",
    color: "primary.contrastText",
    bgcolor: "primary.main",
    py: isMobile ? 1.5 : 2,
    px: isMobile ? 1 : 2,
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 1,
  };

  const getPendingRequests = () => {
    if (!requests) return [];
    const reqKeys = Object.keys(requests);
    return reqKeys
      .map((reqKey) => requests[reqKey])
      .filter((reqData: Request) => reqData.status === "pending");
  };

  const pendingRequests = getPendingRequests();

  if (!requests) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            color: "text.primary",
            textAlign: "center",
            mb: 2,
          }}
        >
          All Requests
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
          }}
          elevation={5}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
              flexDirection: "column",
              gap: 2,
              p: 3,
            }}
          >
            <CircularProgress color="primary" size={40} thickness={4} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              Loading requests...
            </Typography>
          </Box>
        </TableContainer>
      </Box>
    );
  }

  if (pendingRequests.length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            color: "text.primary",
            textAlign: "center",
            mb: 2,
          }}
        >
          All Requests
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
          }}
          elevation={5}
        >
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 3,
              minHeight: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <EmailIcon sx={{ fontSize: 48, color: "grey.400" }} />
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              No pending requests
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ opacity: 0.8 }}
            >
              New requests from all your games will appear here
            </Typography>
          </Box>
        </TableContainer>
      </Box>
    );
  }

  return (
    <>
      <RequestsModal dets={modalDets} />
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mb: 2 }}>
          <EmailIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            All Requests
          </Typography>
          <Chip
            label={pendingRequests.length}
            size="small"
            color="primary"
            sx={{ ml: 1 }}
          />
        </Box>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            maxHeight: isMobile ? 400 : 600,
            "& .MuiTableCell-root": {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
          elevation={5}
        >
          <Table
            stickyHeader
            size={isMobile ? "small" : "medium"}
            sx={{
              "& .MuiTableHead-root": {
                "& .MuiTableCell-root": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  fontWeight: 500,
                  borderBottom: "none",
                },
              },
            }}
          >
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellStyle}>Game Name</TableCell>
                  <TableCell sx={headerCellStyle}>Player Name</TableCell>
                  <TableCell
                    sx={{
                      ...headerCellStyle,
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    Time Added
                  </TableCell>
                  <TableCell sx={headerCellStyle} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingRequests.map((reqData: Request) => (
                  <TableRow
                    key={reqData.id}
                    hover
                    sx={{
                      "&:hover": {
                        bgcolor: "action.hover",
                        cursor: "pointer",
                      },
                      "& .MuiTableCell-root": {
                        py: isMobile ? 1 : 1.5,
                        px: isMobile ? 1 : 2,
                      },
                    }}
                  >
                    <TableCell
                      onClick={() => navigate(`/games/${reqData.gameId}`)}
                      sx={{ cursor: "pointer" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CasinoIcon color="primary" fontSize="small" />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {reqData.gameName}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell
                      onClick={() => navigate(`/profile/${reqData.senderId}`)}
                      sx={{ cursor: "pointer" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PersonIcon color="action" fontSize="small" />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "text.primary",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {reqData.senderName}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(reqData.timeStamp)}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="Message User" placement="top">
                          <IconButton
                            onClick={() => {
                              navigate(`/conversation/${reqData.senderId}`, {
                                state: { receiverName: reqData.senderName },
                              });
                            }}
                            color="warning"
                            size="small"
                          >
                            <ForumIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Accept Request" placement="top">
                          <IconButton
                            onClick={() => {
                              setModalDets({
                                open: true,
                                status: "acceptRequest",
                                reqData,
                              });
                            }}
                            color="success"
                            size="small"
                          >
                            <AddCircleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject Request" placement="top">
                          <IconButton
                            onClick={() => {
                              setModalDets({
                                open: true,
                                status: "declineRequest",
                                reqData,
                              });
                            }}
                            color="error"
                            size="small"
                          >
                            <DeleteForeverIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </>
    );
}

export default RequestsComponents;
